import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth, navHiddenFor, cardHiddenFor, ALL_CARD_KEYS } from "@/context/AuthContext";
import { CARD_DEFS } from "@/pages/Dashboard";
import { WIDGET_GROUPS } from "@/components/DashboardWidgets";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, RotateCcw, Save } from "lucide-react";

const SECTIONS = [
  ["operations", "Operations"], ["stock", "Stock Control"], ["web_sales", "Web Sales"], ["shop_sales", "Shop Sales"],
  ["marketing", "Marketing & Email Marketing"], ["analytics", "Analytics"], ["data", "Data (settings, imports, databases)"],
];
const TILES = [["tile_jobs", "Open jobs tile"], ["tile_shifts", "Shifts today tile"], ["tile_expenses", "Expenses to approve tile"], ["tile_vehicles", "MOT / service due tile"]];

function Check({ on, onClick, label, testid }) {
  return (
    <label className="flex items-center gap-2.5 rounded-lg px-2.5 py-2 hover:bg-slate-50 cursor-pointer text-sm text-slate-700">
      <input type="checkbox" checked={on} onChange={onClick} data-testid={testid} className="w-4 h-4 accent-indigo-600" />
      {label}
    </label>
  );
}

export function RoleViewsTab({ roles }) {
  const { roleViews, reloadRoleViews } = useAuth();
  const editable = roles.filter((r) => r.key !== "super_admin");
  const [roleKey, setRoleKey] = useState(editable[0]?.key || "");
  const [nav, setNav] = useState(new Set());
  const [cards, setCards] = useState(new Set());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setNav(new Set(navHiddenFor(roleKey, roleViews) || []));
    setCards(new Set(cardHiddenFor(roleKey, roleViews) || []));
  }, [roleKey, roleViews]);

  const cardGroups = useMemo(() => [
    { cat: "Welcome tiles", items: TILES.map(([key, label]) => ({ key, label })) },
    { cat: "Core cards", items: CARD_DEFS },
    ...WIDGET_GROUPS,
  ], []);

  const flip = (set, setter, k) => { const n = new Set(set); n.has(k) ? n.delete(k) : n.add(k); setter(n); };
  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/role-views/${roleKey}`, { nav_hidden: [...nav], card_hidden: [...cards].filter((k) => ALL_CARD_KEYS.includes(k)) });
      toast.success("Visibility saved"); reloadRoleViews();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save"); }
    finally { setSaving(false); }
  };
  const reset = async () => {
    try { await api.delete(`/role-views/${roleKey}`); toast.success("Reset to default"); reloadRoleViews(); }
    catch (e) { toast.error("Could not reset"); }
  };

  return (
    <div className="space-y-5" data-testid="role-views-tab">
      <div className="flex flex-wrap items-center gap-3">
        <select value={roleKey} onChange={(e) => setRoleKey(e.target.value)} data-testid="role-views-role-select"
          className="h-10 rounded-xl border border-slate-200 bg-white px-3 text-sm font-medium">
          {editable.map((r) => <option key={r.key} value={r.key}>{r.name}</option>)}
        </select>
        <p className="text-sm text-slate-500 flex-1 min-w-[220px]">Tick what this team can see. Staff with more than one role see anything any of their roles allows. Super Admin always sees everything.</p>
        <Button variant="outline" onClick={reset} className="gap-1.5" data-testid="role-views-reset"><RotateCcw className="w-4 h-4" /> Default</Button>
        <Button onClick={save} disabled={saving || !roleKey} className="gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="role-views-save">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
        </Button>
      </div>
      <div className="grid lg:grid-cols-3 gap-5">
        <section className="bg-white rounded-2xl border border-slate-200 p-4" data-testid="role-views-menus">
          <h3 className="text-sm font-semibold text-slate-900 mb-2">Menus</h3>
          {SECTIONS.map(([k, l]) => <Check key={k} on={!nav.has(k)} onClick={() => flip(nav, setNav, k)} label={l} testid={`role-views-nav-${k}`} />)}
          <p className="text-[11px] text-slate-400 mt-2 px-2.5">Other menus follow the role's permissions.</p>
        </section>
        <section className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 p-4" data-testid="role-views-cards">
          <h3 className="text-sm font-semibold text-slate-900 mb-2">Dashboard cards</h3>
          <div className="grid sm:grid-cols-2 gap-x-4">
            {cardGroups.map((g) => (
              <div key={g.cat} className="mb-3">
                <div className="px-2.5 pt-1 pb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">{g.cat}</div>
                {g.items.map((c) => <Check key={c.key} on={!cards.has(c.key)} onClick={() => flip(cards, setCards, c.key)} label={c.label} testid={`role-views-card-${c.key}`} />)}
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
